import React, { useState, useEffect } from "react";
import { FormFeedback, Tooltip } from "reactstrap";
import { Editor } from "react-draft-wysiwyg";
import { EditorState, ContentState, convertFromHTML, convertToRaw } from "draft-js";
import draftToHtml from "draftjs-to-html";
import DOMPurify from "dompurify";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";

const WYSIWYGEditorInput = ({
    label = "Enter Content",
    placeholder = "Enter your text here",
    required = false,
    layout = "below",
    value = "",
    name = "",
    id="",
    onChange = () => {},
    maxLength = 2000,
    requiredErrorMessage = "This field is required.",
    invalidContentErrorMessage = "Invalid content detected.",
    maxLengthErrorMessage = `You can enter up to ${maxLength} characters only.`,
    showHelp = false,
    helpText = "Use the editor for text formatting.",
    customToolbarOptions = {},
    sanitizeHtml = true,
    allowedTags = ["b", "i", "u", "a", "p"],
    height = "200px", // Default height
}) => {
    const [editorState, setEditorState] = useState(EditorState.createEmpty());
    const [errors, setErrors] = useState({});
    const [tooltipOpen, setTooltipOpen] = useState(false);
    const [charCount, setCharCount] = useState(0);

    useEffect(() => {
        if (value) {
            const blocksFromHTML = convertFromHTML(value);
            const contentState = ContentState.createFromBlockArray(blocksFromHTML.contentBlocks, blocksFromHTML.entityMap);
            setEditorState(EditorState.createWithContent(contentState));
        }
    }, [value]);

    const handleEditorChange = (newState) => {
        const rawContent = draftToHtml(convertToRaw(newState.getCurrentContent()));
        const cleanContent = sanitizeHtml ? DOMPurify.sanitize(rawContent, { ALLOWED_TAGS: allowedTags }) : rawContent;
        const contentLength = cleanContent.replace(/<[^>]*>?/gm, "").length;

        setCharCount(contentLength);

        if (contentLength > maxLength) {
            setErrors({ maxLength: maxLengthErrorMessage });
        } else {
            setErrors({});
        }

        setEditorState(newState);
        onChange({ target: { name, value: cleanContent } });
    };

    return (
        <div className={layout === "inline" ? "d-flex align-items-center" : ""}>
            <label htmlFor={name} className="d-flex" style={{ width: layout === "inline" ? "25%" : "100%" }}>
                {label}
                {required && <span className="text-danger ml-1">*</span>}
                {showHelp && (
                    <span id={`helpTooltip-${id}`} className="ms-1"
                    style={{  position: "relative", top: "-3px" }}
>
                        <i className="ri-question-line" style={{ cursor: 'pointer', fontSize: '16px' }}></i>
                    </span>
                )}
            </label>
            {showHelp && (
                <Tooltip
                    placement="right"
                    isOpen={tooltipOpen}
                    target={`helpTooltip-${id}`}
                    toggle={() => setTooltipOpen(!tooltipOpen)}
                >
                    {helpText}
                </Tooltip>
            )}
            <div className="w-100 border">
                <Editor
                    editorState={editorState}
                    toolbarClassName="toolbar-class"
                    wrapperClassName="wrapper-class"
                    editorClassName="editor-class"
                    placeholder={placeholder}
                    name={name}
                    toolbar={{
                        options: ["inline", "fontSize", "list", "textAlign", "link", "image", "history"],
                        inline: { options: ["bold", "italic", "underline"] },
                        textAlign: { options: ["left", "center", "right", "justify"] },
                        list: { options: ["unordered", "ordered"] },
                        ...customToolbarOptions,
                    }}
                    editorStyle={{ height: height, overflow: "auto", padding:"15px" }}
                    onEditorStateChange={handleEditorChange}
                />
                <div className="d-flex justify-content-between">
                    <span>{charCount}/{maxLength} characters</span>
                </div>
                {errors.required && (
                    <FormFeedback type="invalid" className="d-block">
                        {errors.required}
                    </FormFeedback>
                )}
                {errors.maxLength && (
                    <FormFeedback type="invalid" className="d-block">
                        {errors.maxLength}
                    </FormFeedback>
                )}
                {errors.invalidContent && (
                    <FormFeedback type="invalid" className="d-block">
                        {invalidContentErrorMessage}
                    </FormFeedback>
                )}
            </div>
        </div>
    );
};

export default WYSIWYGEditorInput;
